import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  Plus,
  Eye,
  Edit,
  Trash2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  TrendingUp,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  XCircle,
  X,
  UserCheck,
  Filter,
  Paperclip
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import {
  OVERTIME_TYPE_VALUES,
  LEGACY_OVERTIME_TYPE_MAP,
  normalizeOvertimeType,
  formatPointageDate,
  formatBrandMoney,
  formatBrandMoneyCompact,
  calculateOvertimeAmount,
  calculateHourlyRateFromSalary,
  sumOvertimeHoursForYear,
  SAUDI_MAX_ANNUAL_OVERTIME_HOURS,
  SAUDI_MAX_DAILY_OVERTIME_HOURS,
  SAUDI_OVERTIME_RATE,
  SAUDI_MONTHLY_HOURS_DIVISOR,
} from './pointageHelpers';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildPointageControlsStrip,
  buildPointageImportExportProps,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  mapPointageStatCards,
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEntityReference,
  usePointageEmployeeOptions,
  buildPointageEmployeeSalaryMap,
  resolvePointageEmployeeDisplayName,
  resolvePointageEmployeeFromSelect,
} from './pointageListPageUtils';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import PointageEntityReferenceField from './components/PointageEntityReferenceField';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';

const NS = 'modules.pointage.pages.overtimePage';

const OvertimePage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();

  const getOvertimeTypeLabel = (type) => {
    const normalized = normalizeOvertimeType(type);
    const entry = Object.entries(OVERTIME_TYPE_VALUES).find(([, value]) => value === normalized);
    if (entry) return p(`overtime_types.${entry[0]}`);
    const legacyEntry = Object.entries(LEGACY_OVERTIME_TYPE_MAP).find(([key]) => key === type);
    if (legacyEntry) {
      const legacyKey = Object.entries(OVERTIME_TYPE_VALUES).find(([, v]) => v === legacyEntry[1])?.[0];
      if (legacyKey) return p(`overtime_types.${legacyKey}`);
    }
    return type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      approved: p('status_approved'),
      pending: p('status_pending'),
      rejected: p('status_rejected')
    };
    return labels[status] || p('em_dash');
  };
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  
  // Status filter
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  // Advanced options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Employee options for dropdown (3 in 1: Fetch + Search + Create)
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { status: 'active', valueField: 'id' },
  );
  const employeeSalaryMap = useMemo(
    () => buildPointageEmployeeSalaryMap(employeeOptions),
    [employeeOptions],
  );
  const [employeeConsent, setEmployeeConsent] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOvertime, setSelectedOvertime] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  // Data
  const [overtimes, setOvertimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    totalHours: 0,
    totalAmount: 0,
    pending: 0,
    approved: 0
  });
  const [formData, setFormData] = useState({
    overtime_ref: '',
    employeeName: '',
    employeeId: null,
    date: '',
    hours: '',
    hourlyRate: '',
    type: '',
    reason: '',
    status: ''
  });

  const generateOvertimeRef = usePointageEntityReference('overtime', 'OVT', overtimes, 'overtime_ref');

  const [formTab, setFormTab] = useState('general');

  const OVERTIME_FORM_FIELD_COUNT = 9;

  const overtimeFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_basic_info'), icon: UserCheck },
      { id: 'details', label: p('section_reason_status'), icon: FileText, iconClass: 'text-purple-400' },
    ],
  }, OVERTIME_FORM_FIELD_COUNT), [p]);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchOvertimes();
    fetchStats();
  }, [dateRange]);

  const resolveEmployeeName = (employeeId, fallback = '') =>
    resolvePointageEmployeeDisplayName(employeeId, employeeOptions, fallback);

  const resolveHourlyRate = (employeeId, explicitRate) => {
    const parsed = parseFloat(explicitRate);
    if (parsed > 0) return parsed;
    const salary = employeeSalaryMap[String(employeeId)];
    return calculateHourlyRateFromSalary(salary);
  };

  const getYearlyOvertimeForEmployee = (employeeId, year, excludeId = null) => {
    const y = year || new Date().getFullYear();
    const filtered = overtimes.filter((o) => {
      if (excludeId && o.id === excludeId) return false;
      if (o.status === 'rejected') return false;
      const oid = String(o.employee_id ?? o.employeeId ?? '');
      return oid === String(employeeId);
    });
    const name = resolveEmployeeName(employeeId);
    return sumOvertimeHoursForYear(filtered, y, name);
  };

  const getDailyOvertimeForEmployee = (employeeId, date, excludeId = null) => {
    if (!date) return 0;
    return overtimes
      .filter((o) => {
        if (excludeId && o.id === excludeId) return false;
        if (o.status === 'rejected') return false;
        const oid = String(o.employee_id ?? o.employeeId ?? '');
        return oid === String(employeeId) && o.date === date;
      })
      .reduce((sum, o) => sum + (parseFloat(o.hours) || 0), 0);
  };

  const fetchOvertimes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;

      const data = await localApiAdapter.get('/timetracking/overtime', params);
      setOvertimes(data || []);
    } catch (error) {
      console.error('Error fetching overtimes:', error);
      setOvertimes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await localApiAdapter.get('/timetracking/overtime/stats');
      setStats({
        total: data.total || 0,
        totalHours: data.totalHours || 0,
        totalAmount: data.totalAmount || 0,
        pending: data.pending || 0,
        approved: data.approved || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hours = parseFloat(formData.hours) || 0;
    const hourlyRate = resolveHourlyRate(formData.employeeId, formData.hourlyRate);
    const entryYear = formData.date ? new Date(`${formData.date}T12:00:00`).getFullYear() : new Date().getFullYear();
    const excludeId = selectedOvertime?.id ?? null;

    if (!formData.employeeId || !formData.date || hours <= 0) {
      alert(p('error_required_fields'));
      return;
    }
    if (!employeeConsent && !selectedOvertime) {
      alert(p('error_consent_required'));
      return;
    }
    if (hours > SAUDI_MAX_DAILY_OVERTIME_HOURS) {
      alert(p('error_daily_overtime_max', { max: SAUDI_MAX_DAILY_OVERTIME_HOURS }));
      return;
    }
    const dailyExisting = getDailyOvertimeForEmployee(formData.employeeId, formData.date, excludeId);
    if (dailyExisting + hours > SAUDI_MAX_DAILY_OVERTIME_HOURS) {
      alert(p('error_daily_overtime_cumulative', { max: SAUDI_MAX_DAILY_OVERTIME_HOURS, existing: dailyExisting.toFixed(1) }));
      return;
    }
    const yearlyExisting = getYearlyOvertimeForEmployee(formData.employeeId, entryYear, excludeId);
    if (yearlyExisting + hours > SAUDI_MAX_ANNUAL_OVERTIME_HOURS) {
      alert(p('error_annual_overtime_max', { max: SAUDI_MAX_ANNUAL_OVERTIME_HOURS, existing: yearlyExisting.toFixed(1) }));
      return;
    }

    try {
      setLoading(true);
      const totalAmount = calculateOvertimeAmount(hours, hourlyRate, formData.type || OVERTIME_TYPE_VALUES.regular);
      const overtimeRef = (formData.overtime_ref || '').trim() || generateOvertimeRef();
      const employeeName = resolveEmployeeName(formData.employeeId, formData.employeeName);
      const payload = {
        overtime_ref: overtimeRef,
        employee_id: formData.employeeId,
        employee_name: employeeName,
        date: formData.date,
        hours,
        hourly_rate: hourlyRate,
        overtime_rate: SAUDI_OVERTIME_RATE,
        total_amount: totalAmount,
        type: formData.type || OVERTIME_TYPE_VALUES.regular,
        reason: formData.reason,
        status: formData.status || 'pending',
        employee_consent: employeeConsent,
      };

      if (selectedOvertime) {
        await localApiAdapter.put(`/timetracking/overtime/${selectedOvertime.id}`, payload);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/timetracking/overtime', payload);
        notifyCreated(showNotify, p('title'));
      }

      await fetchOvertimes();
      await fetchStats();
      resetForm();
    } catch (error) {
      console.error('Error saving overtime:', error);
      notifyError(showNotify, p('error_save')); return;
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (overtime) => {
    setSelectedOvertime(overtime);
    setFormData({
      overtime_ref: overtime.overtime_ref || overtime.overtimeRef || '',
      employeeName: overtime.employee_name || overtime.employeeName,
      employeeId: overtime.employee_id != null ? String(overtime.employee_id) : '',
      date: overtime.date,
      hours: overtime.hours,
      hourlyRate: overtime.hourly_rate || overtime.hourlyRate,
      type: overtime.type,
      reason: overtime.reason || '',
      status: overtime.status
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/timetracking/overtime/${id}`);
        notifyDeleted(showNotify, p('title'));
        await fetchOvertimes();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting overtime:', error);
        notifyError(showNotify, p('error_delete'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/timetracking/overtime/${id}`, { status: newStatus });
      await fetchOvertimes();
      await fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const clickableStatusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: AlertTriangle, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ], [p]);

  const statusColumnFilterOptions = useMemo(
    () => clickableStatusOptions.map(({ value, label }) => ({ value, label })),
    [clickableStatusOptions],
  );

  const handleView = (overtime) => {
    setSelectedOvertime(overtime);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ overtime_ref: '', employeeName: '', employeeId: null, date: '', hours: '', hourlyRate: '', type: '', reason: '', status: '' });
    setSelectedOvertime(null);
    setEmployeeConsent(false);
    setFormTab('general');
    setShowModal(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'approved': 'bg-green-500/20 text-green-300 border-green-500/30',
      'pending': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'rejected': 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['pending']}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  const currentYear = new Date().getFullYear();
  const yearlyApprovedHours = useMemo(() => {
    return overtimes
      .filter((o) => o.status !== 'rejected' && overtimeEntryYearSafe(o) === currentYear)
      .reduce((sum, o) => sum + (parseFloat(o.hours) || 0), 0);
  }, [overtimes, currentYear]);

  function overtimeEntryYearSafe(o) {
    if (!o?.date) return null;
    const d = new Date(o.date);
    return Number.isNaN(d.getTime()) ? null : d.getFullYear();
  }

  const monthlyOvertimeSummary = useMemo(() => {
    const map = {};
    overtimes.forEach((o) => {
      if (o.status === 'rejected') return;
      const name = o.employee_name || o.employeeName || p('unknown_employee');
      const d = new Date(o.date);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map[name]) map[name] = {};
      if (!map[name][key]) map[name][key] = { hours: 0, amount: 0 };
      const rate = parseFloat(o.hourly_rate ?? o.hourlyRate) || 0;
      const hours = parseFloat(o.hours) || 0;
      const amount = parseFloat(o.total_amount ?? o.totalAmount) || calculateOvertimeAmount(hours, rate, o.type);
      map[name][key].hours += hours;
      map[name][key].amount += amount;
    });
    return map;
  }, [overtimes, p]);

  const baseFilteredOvertimes = useMemo(() => overtimes.filter(overtime => {
    const matchesSearch = !searchTerm || 
      (overtime.overtime_ref || overtime.overtimeRef || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (overtime.employee_name || overtime.employeeName)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      overtime.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      overtime.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || overtime.status === statusFilter;
    
    const matchesMainView = 
      mainViewMode === 'all' ||
      (mainViewMode === 'pending' && overtime.status === 'pending') ||
      (mainViewMode === 'approved' && overtime.status === 'approved') ||
      (mainViewMode === 'rejected' && overtime.status === 'rejected');
    
    const matchesQuickFilter = 
      quickFilter === 'all' ||
      (quickFilter === 'regular' && (overtime.type === OVERTIME_TYPE_VALUES.regular || overtime.type === 'Normal')) ||
      (quickFilter === 'night' && (overtime.type === OVERTIME_TYPE_VALUES.night || overtime.type === 'Nuit')) ||
      (quickFilter === 'friday' && (overtime.type === OVERTIME_TYPE_VALUES.friday || overtime.type === 'Weekend')) ||
      (quickFilter === 'holiday' && (overtime.type === OVERTIME_TYPE_VALUES.holiday || overtime.type === 'Férié' || overtime.type === 'Ferie'));
    
    const matchesDistribution = 
      !activeDistributionFilter ||
      overtime.status === activeDistributionFilter;
    
    return matchesSearch && matchesStatus && matchesMainView && matchesQuickFilter && matchesDistribution;
  }), [overtimes, searchTerm, statusFilter, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (o) => o.overtime_ref || o.overtimeRef || ''),
    textColumnRule('employee', (o) => o.employee_name || o.employeeName || ''),
    textColumnRule('date', (o) => formatPointageDate(o.date) || ''),
    textColumnRule('hours', (o) => String(o.hours ?? '')),
    textColumnRule('hourly_rate', (o) => formatBrandMoney(o.hourly_rate || o.hourlyRate)),
    textColumnRule('type', (o) => getOvertimeTypeLabel(o.type)),
    textColumnRule('amount', (o) => String(o.total_amount ?? o.totalAmount ?? '')),
    exactColumnRule('status', (o) => o.status || ''),
  ], [getOvertimeTypeLabel]);

  const filteredOvertimes = useMemo(
    () => applyColumnFilters(baseFilteredOvertimes, colFilters, columnFilterRules),
    [baseFilteredOvertimes, colFilters, columnFilterRules],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredOvertimes);

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        setLoading(true);
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/timetracking/overtime/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        await fetchOvertimes();
        await fetchStats();
      } catch (error) {
        console.error('Error bulk deleting overtimes:', error);
        notifyError(showNotify, p('error_bulk_delete'));
      } finally {
        setLoading(false);
      }
    }
  };

  const overtimeKpiCards = [
    { key: 'stat_total_entries', value: stats.total, icon: Clock, color: 'from-blue-500 to-blue-600' },
    { key: 'stat_total_hours', value: p('hours_value', { hours: stats.totalHours }), icon: TrendingUp, color: 'from-green-500 to-green-600' },
    { key: 'stat_total_amount', value: formatBrandMoneyCompact(stats.totalAmount || 0), icon: DollarSign, color: 'from-purple-500 to-purple-600' },
    { key: 'stat_yearly_hours', value: p('yearly_hours_value', { hours: yearlyApprovedHours.toFixed(1), max: SAUDI_MAX_ANNUAL_OVERTIME_HOURS }), icon: AlertTriangle, color: yearlyApprovedHours >= SAUDI_MAX_ANNUAL_OVERTIME_HOURS ? 'from-red-500 to-red-600' : 'from-orange-500 to-orange-600' },
    { key: 'stat_approved', value: stats.approved, icon: CheckCircle, color: 'from-gray-500 to-gray-600' }
  ];

  const mainTabs = [
    { id: 'all', icon: Clock },
    { id: 'pending', icon: AlertTriangle },
    { id: 'approved', icon: CheckCircle },
    { id: 'rejected', icon: X }
  ];

  const quickFilterButtons = [
    { id: 'all', icon: Clock },
    { id: 'regular', icon: Clock },
    { id: 'night', icon: Clock },
    { id: 'friday', icon: Calendar },
    { id: 'holiday', icon: Calendar }
  ];

  const distributionData = [
    { id: 'approved', count: overtimes.filter(o => o.status === 'approved').length, color: 'bg-green-500' },
    { id: 'pending', count: overtimes.filter(o => o.status === 'pending').length, color: 'bg-orange-500' },
    { id: 'rejected', count: overtimes.filter(o => o.status === 'rejected').length, color: 'bg-red-500' }
  ];

  const listStats = mapPointageStatCards(
    overtimeKpiCards.slice(0, 4).map((stat) => ({
      key: stat.key,
      label: p(stat.key),
      value: stat.value,
      icon: stat.icon,
      color: stat.color,
    }))
  );

  const importExportProps = buildPointageImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
  });

  const openCreateModal = () => {
    setSelectedOvertime(null);
    setFormData({
      overtime_ref: generateOvertimeRef(),
      employeeName: '',
      employeeId: null,
      date: '',
      hours: '',
      hourlyRate: '',
      type: '',
      reason: '',
      status: '',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const advancedOptionsContent = (
    <div className="space-y-4 pt-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setStatusFilter(tab.id === 'all' ? 'all' : tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); setIsStatusFilterOpen(false); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(`tab_${tab.id}`)}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(`filter_${filter.id}`)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{p('distribution_by_status')}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <X size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {distributionData.map((item) => (
            <button key={item.id} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.id ? null : item.id)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.id ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === item.id ? 'text-white' : 'text-white/80'}`}>{p(`dist_${item.id}`)}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'code', labelKey: 'col_code' },
    { key: 'employee', labelKey: 'col_employee' },
    { key: 'date', labelKey: 'col_date' },
    { key: 'hours', labelKey: 'col_hours' },
    { key: 'hourly_rate', labelKey: 'col_hourly_rate' },
    { key: 'type', label: t('common.type') },
    { key: 'amount', labelKey: 'col_amount' },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  // ===== RENDER =====
  return (
    <>
      <FacturationListPageLayout
        className="space-y-6"
        containerClassName="clients-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Clock}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildPointageBulkDeleteFilterAction({
          count: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: `${t('common.delete')} (${selectedRecords.length})`,
          title: t('common.delete', { defaultValue: 'حذف' }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={openCreateModal}
        createLabel={p('new_overtime')}
        importExport={importExportProps}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildPointageControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        <p className="px-4 pt-2 pb-2 text-white/50 text-sm">{p('saudi_law_hint', { rate: '150%', maxAnnual: SAUDI_MAX_ANNUAL_OVERTIME_HOURS, maxDaily: SAUDI_MAX_DAILY_OVERTIME_HOURS, divisor: SAUDI_MONTHLY_HOURS_DIVISOR })}</p>

        {viewMode === 'table' ? (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('code') && (
                    <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={colFilters.code ?? ''} onChange={(v) => setColFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('employee') && (
                    <TableColumnHeaderFilter columnKey="employee" label={p('col_employee')} value={colFilters.employee ?? ''} onChange={(v) => setColFilter('employee', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('date') && (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={colFilters.date ?? ''} onChange={(v) => setColFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('hours') && (
                    <TableColumnHeaderFilter columnKey="hours" label={p('col_hours')} value={colFilters.hours ?? ''} onChange={(v) => setColFilter('hours', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('hourly_rate') && (
                    <TableColumnHeaderFilter columnKey="hourly_rate" label={p('col_hourly_rate')} value={colFilters.hourly_rate ?? ''} onChange={(v) => setColFilter('hourly_rate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('type') && (
                    <TableColumnHeaderFilter columnKey="type" label={t('common.type')} value={colFilters.type ?? ''} onChange={(v) => setColFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('amount') && (
                    <TableColumnHeaderFilter columnKey="amount" label={p('col_amount')} value={colFilters.amount ?? ''} onChange={(v) => setColFilter('amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {filteredOvertimes.length > 0 ? (
                  filteredOvertimes.map((overtime) => (
                    <tr key={overtime.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(overtime.id)}
                        onChange={() => handleSelectRecord(overtime.id)}
                      />
                      {isColVisible('code') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold font-mono">{overtime.overtime_ref || overtime.overtimeRef || '—'}</span></td>
                      )}
                      {isColVisible('employee') && (
                        <td className="px-6 py-4 text-white font-semibold">{overtime.employee_name || overtime.employeeName}</td>
                      )}
                      {isColVisible('date') && (
                        <td className="px-6 py-4 text-white/80">{formatPointageDate(overtime.date)}</td>
                      )}
                      {isColVisible('hours') && (
                        <td className="px-6 py-4 text-white font-medium">{p('hours_value', { hours: overtime.hours })}</td>
                      )}
                      {isColVisible('hourly_rate') && (
                        <td className="px-6 py-4 text-white/80">{formatBrandMoney(overtime.hourly_rate || overtime.hourlyRate)}</td>
                      )}
                      {isColVisible('type') && (
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-medium">
                            {getOvertimeTypeLabel(overtime.type)}
                          </span>
                        </td>
                      )}
                      {isColVisible('amount') && (
                        <td className="px-6 py-4 text-white font-bold">{formatBrandMoney(overtime.total_amount || overtime.totalAmount || calculateOvertimeAmount(overtime.hours, overtime.hourly_rate || overtime.hourlyRate, overtime.type))}</td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={overtime.status}
                              recordId={overtime.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onView={() => handleView(overtime)}
                            onEdit={() => handleEdit(overtime)}
                            onDelete={() => handleDelete(overtime.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center text-white/50">
                      {p('empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredOvertimes.map((overtime) => (
                <motion.div
                  key={overtime.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl shadow-black/10 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap">
                        {(overtime.overtime_ref || overtime.overtimeRef) && (
                          <span className="px-2 py-1 bg-white/10 text-white/80 rounded text-xs font-mono">{overtime.overtime_ref || overtime.overtimeRef}</span>
                        )}
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-medium">
                          {getOvertimeTypeLabel(overtime.type)}
                        </span>
                        {getStatusBadge(overtime.status)}
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-1 truncate">{overtime.employeeName}</h3>
                      <p className="text-white/60 text-sm">{formatPointageDate(overtime.date)}</p>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(overtime)}
                        onEdit={() => handleEdit(overtime)}
                        onDelete={() => handleDelete(overtime.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('col_hours')}</span>
                      <span className="text-white font-semibold text-lg">{p('hours_value', { hours: overtime.hours || 0 })}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('col_hourly_rate')}</span>
                      <span className="text-white font-semibold">{formatBrandMoney(overtime.hourly_rate || overtime.hourlyRate || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2">
                      <span className="text-white/50 text-xs">{p('card_total_amount')}</span>
                      <span className="text-white font-bold text-lg">{formatBrandMoney(overtime.total_amount || overtime.totalAmount || calculateOvertimeAmount(overtime.hours, overtime.hourly_rate || overtime.hourlyRate, overtime.type))}</span>
                    </div>
                  </div>

                  {overtime.reason && (
                    <div className="bg-white/5 rounded-lg p-3 mt-4">
                      <p className="text-white/50 text-xs mb-1">{p('label_reason')}</p>
                      <p className="text-white text-sm">{overtime.reason}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </FacturationListPageLayout>

      {Object.keys(monthlyOvertimeSummary).length > 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10 p-6">
          <h3 className="text-xl font-semibold text-white mb-4">{p('monthly_summary_title')}</h3>
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className="px-4 py-3 text-left text-white/80 text-sm">{t('common.employee')}</th>
                  <th className="px-4 py-3 text-left text-white/80 text-sm">{p('summary_period')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_hours')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_amount')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(monthlyOvertimeSummary).flatMap(([name, periods]) =>
                  Object.entries(periods).map(([period, data]) => (
                    <tr key={`${name}-${period}`} className="border-t border-white/10 hover:bg-white/5">
                      <td className="px-4 py-3 text-white font-medium">{name}</td>
                      <td className="px-4 py-3 text-white/70">{period}</td>
                      <td className="px-4 py-3 text-white text-right">{p('hours_value', { hours: data.hours.toFixed(1) })}</td>
                      <td className="px-4 py-3 text-white font-semibold text-right">{formatBrandMoney(data.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal — coquille « Ajouter un Pointage » */}
      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={selectedOvertime ? p('modal_edit_title') : p('modal_new_title')}
          icon={Clock}
          onSubmit
          formId="overtime-form"
          saveLabel={selectedOvertime ? p('update') : t('common.save')}
          maxWidth="1000px"
          testId="overtime-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={overtimeFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="overtime-form"
            onSubmit={handleSubmit}
            ariaLabel={selectedOvertime ? p('modal_edit_title') : p('modal_new_title')}
          >
            {pointageFormTabVisible(overtimeFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <PointageEntityReferenceField
                  label={p('field_code')}
                  value={formData.overtime_ref}
                  onChange={(v) => setFormData({ ...formData, overtime_ref: v })}
                  onRegenerate={selectedOvertime ? undefined : () => setFormData({ ...formData, overtime_ref: generateOvertimeRef() })}
                  placeholder={p('ph_code')}
                  autoGeneratedHint={selectedOvertime ? undefined : p('auto_ref_hint', { defaultValue: 'يُولَّد تلقائياً' })}
                  regenerateTitle={t('common.regenerate', { defaultValue: 'إعادة توليد الرمز' })}
                  inputClassName="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  labelClassName={modalForm.label}
                />
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_employee')} />}</label>
                  <CustomSelect
                    value={formData.employeeId ? String(formData.employeeId) : ''}
                    onChange={(v) => {
                      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(v, employeeOptions);
                      const salary = employeeSalaryMap[String(employeeId)];
                      const autoRate = calculateHourlyRateFromSalary(salary);
                      setFormData({
                        ...formData,
                        employeeId,
                        employeeName,
                        hourlyRate: autoRate > 0 ? autoRate.toFixed(2) : formData.hourlyRate,
                      });
                    }}
                    options={employeeOptions}
                    placeholder={p('employee_placeholder')}
                    searchable={true}
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_type_required')} />}</label>
                  <CustomSelect
                    value={formData.type}
                    onChange={(v) => setFormData({ ...formData, type: v })}
                    options={Object.entries(OVERTIME_TYPE_VALUES).map(([key, value]) => ({
                      value,
                      label: p(`overtime_types.${key}`)
                    }))}
                    placeholder={p('label_type')}
                    searchable={true}
                    creatable={true}
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_date_required')} />}</label>
                  <CustomDatePicker
                    value={formData.date}
                    onChange={(v) => setFormData({ ...formData, date: v })}
                    placeholder={p('date_placeholder')}
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div className={POINTAGE_FORM_GRID_CLASS}>
                  <div>
                    <label className={modalForm.label}>{<RequiredLabelText text={p('label_hours_required')} />}</label>
                    <CustomNumberInput
                      value={formData.hours}
                      onChange={(v) => setFormData({ ...formData, hours: v === '' ? '' : v })}
                      step={0.5}
                      min={0}
                      max={SAUDI_MAX_DAILY_OVERTIME_HOURS}
                      decimals={2}
                      placeholder={p('hours_placeholder')}
                    />
                    <p className="text-white/40 text-xs mt-1">{p('daily_overtime_max_hint', { max: SAUDI_MAX_DAILY_OVERTIME_HOURS })}</p>
                  </div>
                  <div>
                    <label className={modalForm.label}>{<RequiredLabelText text={p('label_hourly_rate_required')} />}</label>
                    <CustomNumberInput
                      value={formData.hourlyRate}
                      onChange={(v) => setFormData({ ...formData, hourlyRate: v === '' ? '' : v })}
                      step={0.01}
                      min={0}
                      decimals={2}
                      placeholder={p('hourly_rate_placeholder')}
                    />
                    <p className="text-white/40 text-xs mt-1">{p('hourly_rate_formula', { divisor: SAUDI_MONTHLY_HOURS_DIVISOR })}</p>
                  </div>
                </div>
                {formData.hours && (
                  <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-400/30">
                    <p className="text-white/70 text-sm">{p('computed_overtime_pay')}</p>
                    <p className="text-white font-bold text-xl mt-1">
                      {formatBrandMoney(calculateOvertimeAmount(
                        formData.hours,
                        resolveHourlyRate(formData.employeeId, formData.hourlyRate),
                        formData.type || OVERTIME_TYPE_VALUES.regular
                      ))}
                    </p>
                    <p className="text-white/50 text-xs mt-1">{p('overtime_rate_hint', { rate: '150%' })}</p>
                  </div>
                )}
              </div>
              </div>
            )}

            {pointageFormTabVisible(overtimeFormTabs, formTab, 'details') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_reason')}</label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows={3}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40"
                    placeholder={p('reason_placeholder')}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={employeeConsent}
                      onChange={(e) => setEmployeeConsent(e.target.checked)}
                      className="mt-1 rounded border-white/30 bg-white/10 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-white/80 text-sm">{p('label_employee_consent')}</span>
                  </label>
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_status')}</label>
                  <CustomSelect
                    value={formData.status}
                    onChange={(v) => setFormData({ ...formData, status: v })}
                    options={[
                      { value: 'pending', label: p('status_pending') },
                      { value: 'approved', label: p('status_approved') },
                      { value: 'rejected', label: p('status_rejected') }
                    ]}
                    placeholder={p('status_placeholder')}
                    searchable={true}
                    creatable={true}
                    autoSelect={false}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_attachment')}</label>
                  <input
                    type="file"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500/20 file:text-blue-300 hover:file:bg-blue-500/30"
                  />
                </div>
              </div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}

      {/* View Modal */}
      {showViewModal && selectedOvertime && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="640px"
          testId="overtime-view-modal"
        >
          <div className="space-y-4 space-y-4">
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('col_code')}</p>
                <p className="text-white font-semibold font-mono text-lg">{selectedOvertime.overtime_ref || selectedOvertime.overtimeRef || '—'}</p>
              </div>
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{t('common.employee')}</p>
                <p className="text-white font-semibold text-lg">{selectedOvertime.employee_name || selectedOvertime.employeeName}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{t('common.date')}</p>
                  <p className={modalForm.viewValue}>{formatPointageDate(selectedOvertime.date)}</p>
                </div>
                <div className={modalForm.viewCard}>
                  <p className="text-white/60 text-sm mb-2">{t('common.type')}</p>
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-sm font-medium">
                    {getOvertimeTypeLabel(selectedOvertime.type)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('col_hours')}</p>
                  <p className="text-white font-bold text-2xl">{p('hours_value', { hours: selectedOvertime.hours })}</p>
                </div>
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('col_hourly_rate')}</p>
                  <p className={modalForm.viewValue}>{formatBrandMoney(selectedOvertime.hourly_rate || selectedOvertime.hourlyRate)}</p>
                </div>
              </div>
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('view_total_amount')}</p>
                <p className="text-white font-bold text-3xl">{formatBrandMoney(selectedOvertime.total_amount || selectedOvertime.totalAmount || calculateOvertimeAmount(selectedOvertime.hours, selectedOvertime.hourly_rate || selectedOvertime.hourlyRate, selectedOvertime.type))}</p>
              </div>
              {selectedOvertime.reason && (
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('label_reason')}</p>
                  <p className="text-white text-sm">{selectedOvertime.reason}</p>
                </div>
              )}
              <div className={modalForm.viewCard}>
                <p className="text-white/60 text-sm mb-2">{t('common.status')}</p>
                {getStatusBadge(selectedOvertime.status)}
              </div>
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20 transition-all"
              >
                {t('common.close')}
              </button>
            </div>
        </QawaemErpViewModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default OvertimePage;


